import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { RefreshCw, Activity, Radio, Zap, Volume2, VolumeX, Star, ArrowDownWideNarrow, ArrowUpNarrowWide, ListFilter, TrendingUp, Bitcoin, GitCompare } from "lucide-react";
import { cn } from "@/lib/utils";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { playSignalSound, isSoundEnabled, setSoundEnabled } from "@/lib/sound";
import { useOverlapAlert } from "@/lib/marketSessions";
import { Link, useNavigate } from "react-router-dom";
import LiveSignalCard from "./LiveSignalCard";
import LanguageSwitcher from "./LanguageSwitcher";
import NotificationSettings from "./NotificationSettings";
import TopPicks from "./TopPicks";
import SensitivitySelector from "./SensitivitySelector";
import UserMenu from "./UserMenu";
import CommandSearch from "./CommandSearch";
import MarketClock from "./MarketClock";
import PersonalBestHours from "./PersonalBestHours";
import SubscriptionBadge from "./SubscriptionBadge";
import DemoBanner from "./DemoBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TIMEFRAMES = ["1m", "5m", "15m", "30m", "1h", "4h", "1d"];

export default function Dashboard() {
  const { t, lang, dir } = useI18n();
  const { user, favorites } = useAuth();
  const [signals, setSignals] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [timeframe, setTimeframe] = useState("5m");
  const [showFavOnly, setShowFavOnly] = useState(false);
  const [assetType, setAssetType] = useState("all");   // all | forex | crypto
  const [compareSelection, setCompareSelection] = useState([]);  // up to 2
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState(() => {
    try { return localStorage.getItem("forex.sort") || "default"; } catch { return "default"; }
  });
  const [sensitivity, setSensitivity] = useState(() => {
    try { return localStorage.getItem("forex.sens") || "balanced"; } catch { return "balanced"; }
  });
  const [auto, setAuto] = useState(true);
  const [sound, setSound] = useState(() => isSoundEnabled());
  const pollRef = useRef(null);
  const prevSignalsRef = useRef({});

  // London × NY overlap alert — once per day
  useOverlapAlert(() => {
    toast.success(`${t("overlap_title")} · ${t("overlap_desc")}`, { duration: 10000 });
    playSignalSound("buy");
  });

  const loadStats = useCallback(async (tf) => {
    try {
      const { data } = await axios.get(`${API}/stats`, { params: { timeframe: tf } });
      const map = {};
      (data.stats || []).forEach(s => { map[s.symbol] = s; });
      setStats(map);
    } catch { /* ignore */ }
  }, []);

  const load = useCallback(async (tf) => {
    try {
      setRefreshing(true);
      const { data } = await axios.get(`${API}/live`, { params: { timeframe: tf } });
      const arr = data || [];

      // detect signal changes vs previous fetch → play sound
      const prev = prevSignalsRef.current;
      const changed = [];
      arr.forEach(s => {
        const p = prev[`${s.symbol}::${tf}`];
        if (p && p !== s.signal) changed.push(s.signal);
        prev[`${s.symbol}::${tf}`] = s.signal;
      });
      if (changed.length > 0) {
        // pick the strongest one to play (buy/sell over hold)
        const priority = changed.find(x => x === "buy") || changed.find(x => x === "sell") || "hold";
        playSignalSound(priority);
      }

      setSignals(arr);
      setUpdatedAt(new Date());
      loadStats(tf);
    } catch (e) {
      const msg = e?.response?.data?.detail || t("no_data");
      toast.error(msg);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [t, loadStats]);

  useEffect(() => {
    setLoading(true);
    load(timeframe);
  }, [timeframe, load]);

  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (auto) {
      const interval = timeframe === "1m" ? 25000 : timeframe === "5m" ? 45000 : 60000;
      pollRef.current = setInterval(() => load(timeframe), interval);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [auto, timeframe, load]);

  const counts = signals.reduce(
    (acc, s) => { acc[s.signal] = (acc[s.signal] || 0) + 1; return acc; },
    { buy: 0, sell: 0, hold: 0 }
  );

  const localeCode = { ar: "ar-EG", en: "en-US", de: "de-DE", fr: "fr-FR", es: "es-ES", it: "it-IT", ru: "ru-RU", tr: "tr-TR", pt: "pt-BR", zh: "zh-CN", bg: "bg-BG" }[lang] || "en-US";

  return (
    <div className="relative min-h-screen grid-bg" data-testid="dashboard">
      <DemoBanner />
      <header className="relative border-b border-zinc-900 bg-[#0A0A0A]/85 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-sm bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
              <Activity className="h-5 w-5 text-emerald-400" />
            </div>
            <div>
              <h1 className="text-lg font-bold tracking-tight">{t("app_title")}</h1>
              <p className="text-xs text-zinc-500 flex items-center gap-1.5">
                <Radio className="h-3 w-3 text-emerald-400 animate-pulse" />
                {t("app_subtitle")}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-4 text-xs">
              <SignalCounter label={t("buy_count")} value={counts.buy} dot="bg-emerald-400" />
              <SignalCounter label={t("sell_count")} value={counts.sell} dot="bg-red-400" />
              <SignalCounter label={t("hold_count")} value={counts.hold} dot="bg-amber-400" />
            </div>
            <Button
              onClick={() => setAuto(a => !a)}
              variant="outline"
              size="sm"
              className={cn(
                "border-zinc-700 hover:bg-zinc-900 hover:text-white rounded-sm",
                auto && "border-emerald-500/40 text-emerald-400 hover:text-emerald-300"
              )}
              data-testid="auto-toggle"
            >
              <Zap className={cn("h-3.5 w-3.5", dir === "rtl" ? "ms-2" : "me-2", auto && "fill-emerald-400")} />
              {auto ? t("auto") : t("manual")}
            </Button>
            <CommandSearch pairs={signals} />
            <Button
              onClick={() => load(timeframe)}
              disabled={refreshing}
              variant="outline"
              size="sm"
              className="border-zinc-700 hover:bg-zinc-900 hover:text-white rounded-sm"
              data-testid="refresh-button"
            >
              <RefreshCw className={cn("h-3.5 w-3.5", dir === "rtl" ? "ms-2" : "me-2", refreshing && "animate-spin")} />
              {refreshing ? t("refreshing") : t("refresh")}
            </Button>
            <Button
              onClick={() => {
                const next = !sound;
                setSound(next);
                setSoundEnabled(next);
                if (next) playSignalSound("hold");  // audible confirmation
              }}
              variant="outline"
              size="sm"
              className={cn(
                "border-zinc-700 hover:bg-zinc-900 hover:text-white rounded-sm gap-2",
                sound && "border-emerald-500/40 text-emerald-400 hover:text-emerald-300"
              )}
              data-testid="sound-toggle"
              title={sound ? t("sound_on") : t("sound_off")}
            >
              {sound ? <Volume2 className="h-3.5 w-3.5" /> : <VolumeX className="h-3.5 w-3.5" />}
            </Button>
            <NotificationSettings />
            <LanguageSwitcher />
            <SubscriptionBadge />
            <UserMenu />
          </div>
        </div>

        <div className="border-t border-zinc-900 bg-[#0A0A0A]/60">
          <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-2 overflow-x-auto" data-testid="timeframe-selector">
            <span className="text-[11px] text-zinc-500 ms-1 me-1 whitespace-nowrap">{t("candle_frame")}</span>
            {TIMEFRAMES.map(tf => (
              <button
                key={tf}
                onClick={() => setTimeframe(tf)}
                data-testid={`timeframe-${tf}`}
                className={cn(
                  "px-3 py-1.5 rounded-sm text-xs font-bold transition-all whitespace-nowrap border",
                  timeframe === tf
                    ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/40"
                    : "bg-zinc-900/40 text-zinc-400 border-zinc-800 hover:border-zinc-700 hover:text-white"
                )}
              >
                {t(`tf_${tf}`)}
              </button>
            ))}
            {user && (
              <button
                onClick={() => setShowFavOnly(v => !v)}
                data-testid="favorites-filter"
                className={cn(
                  "ms-2 px-3 py-1.5 rounded-sm text-xs font-bold transition-all whitespace-nowrap border flex items-center gap-1.5",
                  showFavOnly
                    ? "bg-amber-500/15 text-amber-400 border-amber-500/40"
                    : "bg-zinc-900/40 text-zinc-400 border-zinc-800 hover:border-zinc-700 hover:text-white"
                )}
              >
                <Star className={cn("h-3 w-3", showFavOnly && "fill-amber-400")} />
                {t("favorites")}
                {favorites.size > 0 && (
                  <span className="num text-[10px] opacity-80">{favorites.size}</span>
                )}
              </button>
            )}

            <SortControl value={sortBy} onChange={(v) => {
              setSortBy(v);
              try { localStorage.setItem("forex.sort", v); } catch {}
            }} />
            <AssetTypeFilter value={assetType} onChange={setAssetType} />
            <div className="ms-auto flex items-center gap-3">
              <SensitivitySelector
                value={sensitivity}
                onChange={(v) => {
                  setSensitivity(v);
                  try { localStorage.setItem("forex.sens", v); } catch {}
                }}
              />
              <div className="text-[10px] text-zinc-600 whitespace-nowrap">
                {updatedAt ? `${t("last_update")} ${updatedAt.toLocaleTimeString(localeCode)}` : "—"}
              </div>
            </div>
          </div>
        </div>
      </header>

      <section className="border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-6 py-10">
          <div className="flex items-center gap-2 text-xs text-emerald-400 mb-3">
            <Radio className="h-3.5 w-3.5 animate-pulse" />
            <span>{t("market_open")}</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight max-w-3xl">
            {t("hero_title")}
            <span className="block text-zinc-500 font-normal text-lg mt-3">
              {t("hero_sub")}
            </span>
          </h2>
        </div>
      </section>

      <MarketClock />

      <TopPicks timeframe={timeframe} sensitivity={sensitivity} />

      <PersonalBestHours timeframe={timeframe} />

      <main className="max-w-7xl mx-auto px-6 py-10">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {Array.from({ length: 9 }).map((_, i) => (
              <Skeleton key={i} className="h-80 bg-zinc-900 rounded-sm" />
            ))}
          </div>
        ) : signals.length === 0 ? (
          <div className="text-center py-20 text-zinc-500">{t("no_data")}</div>
        ) : (
          (() => {
            let visible = showFavOnly ? signals.filter(s => favorites.has(s.symbol)) : signals.slice();
            // Asset-type filter
            if (assetType === "forex") visible = visible.filter(s => !s.symbol.endsWith("/USDT"));
            else if (assetType === "crypto") visible = visible.filter(s => s.symbol.endsWith("/USDT"));

            // Sort
            if (sortBy !== "default") {
              const dir = sortBy.endsWith("_desc") ? -1 : 1;
              visible.sort((a, b) => {
                let va, vb;
                if (sortBy.startsWith("wr_")) {
                  va = stats[a.symbol]?.win_rate;
                  vb = stats[b.symbol]?.win_rate;
                } else if (sortBy.startsWith("conf_")) {
                  va = a.confidence; vb = b.confidence;
                } else if (sortBy.startsWith("change_")) {
                  va = a.change_pct; vb = b.change_pct;
                }
                if (va == null && vb == null) return 0;
                if (va == null) return 1;
                if (vb == null) return -1;
                return (va - vb) * dir;
              });
            }
            if ((showFavOnly || assetType !== "all") && visible.length === 0) {
              return (
                <div className="text-center py-20 text-zinc-500 flex flex-col items-center gap-3">
                  <Star className="h-8 w-8 text-zinc-700" />
                  <p>{t("favorites_empty")}</p>
                </div>
              );
            }
            return (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="pairs-grid">
                {visible.map((s, i) => (
                  <LiveSignalCard key={s.symbol} signal={s} index={i} timeframe={timeframe} stats={stats[s.symbol]} />
                ))}
              </div>
            );
          })()
        )}

        <footer className="mt-16 pt-8 border-t border-zinc-900 text-[11px] text-zinc-600 leading-relaxed">
          <p className="mb-1">{t("disclaimer")}</p>
          <p>{t("footer_data")}</p>
        </footer>
      </main>
    </div>
  );
}

function SignalCounter({ label, value, dot }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
      <span className="text-zinc-500">{label}</span>
      <span className="num text-white font-bold">{value}</span>
    </div>
  );
}

function SortControl({ value, onChange }) {
  const { t } = useI18n();
  const options = [
    { key: "default",      Icon: ListFilter,          label: "sort_default" },
    { key: "wr_desc",      Icon: ArrowDownWideNarrow, label: "sort_wr_desc" },
    { key: "wr_asc",       Icon: ArrowUpNarrowWide,   label: "sort_wr_asc" },
    { key: "conf_desc",    Icon: ArrowDownWideNarrow, label: "sort_conf_desc" },
    { key: "change_desc",  Icon: ArrowDownWideNarrow, label: "sort_change_desc" },
    { key: "change_asc",   Icon: ArrowUpNarrowWide,   label: "sort_change_asc" },
  ];
  return (
    <div className="flex items-center gap-1.5" data-testid="sort-control">
      <span className="text-[11px] text-zinc-500 whitespace-nowrap flex items-center gap-1">
        <ListFilter className="h-3 w-3" />
        {t("sort_by")}:
      </span>
      <div className="flex items-center gap-1 rounded-sm bg-zinc-900/60 border border-zinc-800 p-0.5 flex-wrap">
        {options.map(({ key, Icon, label }) => (
          <button
            key={key}
            onClick={() => onChange(key)}
            data-testid={`sort-${key}`}
            title={t(label)}
            className={cn(
              "px-2 py-1 rounded-sm text-[11px] font-bold transition-all whitespace-nowrap border flex items-center gap-1",
              value === key
                ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/40"
                : "border-transparent text-zinc-500 hover:text-white"
            )}
          >
            <Icon className="h-3 w-3" />
            <span className="hidden xl:inline">{t(label)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function AssetTypeFilter({ value, onChange }) {
  const { t } = useI18n();
  const options = [
    { key: "all",    Icon: ListFilter,  label: "type_all" },
    { key: "forex",  Icon: TrendingUp,  label: "type_forex" },
    { key: "crypto", Icon: Bitcoin,     label: "type_crypto" },
  ];
  return (
    <div className="flex items-center gap-1.5" data-testid="asset-type-filter">
      <span className="text-[11px] text-zinc-500 whitespace-nowrap">{t("filter_type")}:</span>
      <div className="flex items-center gap-1 rounded-sm bg-zinc-900/60 border border-zinc-800 p-0.5">
        {options.map(({ key, Icon, label }) => (
          <button
            key={key}
            onClick={() => onChange(key)}
            data-testid={`asset-${key}`}
            className={cn(
              "px-2 py-1 rounded-sm text-[11px] font-bold transition-all whitespace-nowrap border flex items-center gap-1",
              value === key
                ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/40"
                : "border-transparent text-zinc-500 hover:text-white"
            )}
          >
            <Icon className="h-3 w-3" />
            <span className="hidden lg:inline">{t(label)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
